import React from 'react'
import tw from 'twin.macro'
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { StatusChip } from 'src/components/payments-ops/StatusChip'
import { RowActionsMenu } from 'src/components/shared/grid/RowActionsMenu'
import {
  capturedEventSourceLabels,
  capturedEventStatusLabels,
} from 'src/constants/payments-ops/labels'
import type { CapturedEventSource, CapturedEventStatus } from 'src/constants/payments-ops/enums'
import type { MenuActionItem } from 'src/interfaces/ui'
import type { CapturedEvent } from 'src/interfaces/usBankKillswitch'
import { formatDateTime, parseFlexibleDateTime } from 'src/utils/grid'
import { resolvePartnerName } from 'src/utils/paymentsOps/partnerNames'

interface CapturedEventColumnOptions {
  nameMap: Record<string, string>
  buildActions(event: CapturedEvent): MenuActionItem[]
}

const renderMonoCell = (params: GridRenderCellParams<CapturedEvent, string | null>) => {
  return <span css={tw`truncate font-mono text-xs`}>{params.value ?? '—'}</span>
}

export const buildCapturedEventColumns = ({
  nameMap,
  buildActions,
}: CapturedEventColumnOptions): Array<GridColDef<CapturedEvent>> => {
  return [
    {
      field: 'capturedAt',
      headerName: 'Captured at',
      width: 180,
      sortable: false,
      valueGetter: parseFlexibleDateTime,
      valueFormatter: formatDateTime,
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 190,
      sortable: false,
      valueGetter: (value: CapturedEventSource) => {
        return capturedEventSourceLabels[value] ?? value
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<CapturedEvent, CapturedEventStatus>) => {
        const status = params.value ? capturedEventStatusLabels[params.value] : undefined

        return status ? <StatusChip label={status.label} tone={status.tone} /> : <span>—</span>
      },
    },
    {
      field: 'partnerId',
      headerName: 'Partner',
      flex: 1,
      minWidth: 160,
      sortable: false,
      valueGetter: (value: string | null) => {
        return resolvePartnerName(nameMap, value)
      },
    },
    {
      field: 'earningId',
      headerName: 'Earning ID',
      width: 160,
      sortable: false,
      display: 'flex',
      renderCell: renderMonoCell,
    },
    {
      field: 'transactionId',
      headerName: 'Transaction ID',
      width: 160,
      sortable: false,
      display: 'flex',
      renderCell: renderMonoCell,
    },
    {
      field: 'notes',
      headerName: 'Notes',
      flex: 1,
      minWidth: 160,
      sortable: false,
      valueGetter: (value: string | null) => {
        return value ?? '—'
      },
    },
    {
      field: 'replayedAt',
      headerName: 'Replayed at',
      width: 180,
      sortable: false,
      valueGetter: parseFlexibleDateTime,
      valueFormatter: formatDateTime,
    },
    {
      field: 'replayedBy',
      headerName: 'Replayed by',
      width: 180,
      sortable: false,
      valueGetter: (value: string | null) => {
        return value ?? '—'
      },
    },
    {
      field: 'replayError',
      headerName: 'Replay error',
      flex: 1,
      minWidth: 180,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<CapturedEvent, string | null>) => {
        return (
          <span css={params.value ? tw`truncate text-critical-500` : undefined}>
            {params.value ?? '—'}
          </span>
        )
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 70,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<CapturedEvent>) => {
        return (
          <RowActionsMenu
            items={buildActions(params.row)}
            ariaLabel="Captured event actions"
            dataTestId={`captured-event-actions-${params.row.id}`}
          />
        )
      },
    },
  ]
}
